import { Application } from "@hotwired/stimulus"
import { identifierForGlobKey } from 'stimulus-vite-helpers'

// LAZY Stimulus registry. The usual `registerControllers(app, import.meta.globEager(...))`
// would eagerly import every controller (and its deps — tinymce alone is ~0.5MB) on every
// page. Instead we glob lazily and only import/register a controller the first time its
// identifier actually appears in the DOM. Three triggers cover all arrival paths:
//   1. initial scan (module runs after DOM parse — it's an ESM bundle, so the DOM is ready;
//      NEVER wait on DOMContentLoaded here, it may already have fired),
//   2. turbo:load (Frames/visits — Drive is off app-wide but Frames are still used),
//   3. a MutationObserver, because plenty of markup is injected AFTER load (Vue-rendered
//      lists, jQuery-inserted modals/partials, cocoon nested fields). Without it, a
//      data-controller inside injected HTML would never boot its controller. Stimulus
//      itself picks up new elements for ALREADY-registered identifiers; the observer is
//      only needed to trigger the first lazy import of not-yet-seen ones.
const application = Application.start()
const controllerLoaders = import.meta.glob('./**/*_controller.js')
const registeredControllers = new Set()
const controllerPathsByIdentifier = Object.fromEntries(
  Object.keys(controllerLoaders)
    .map(path => [identifierForGlobKey(path), path])
    .filter(([identifier]) => identifier)
)

function addIdentifiersFromValue(identifiers, value) {
  if (!value) return

  value.split(/\s+/).filter(Boolean).forEach(identifier => identifiers.add(identifier))
}

function controllerIdentifiersIn(element) {
  const identifiers = new Set()

  if (element.dataset) addIdentifiersFromValue(identifiers, element.dataset.controller)

  element.querySelectorAll('[data-controller]').forEach(controllerElement => {
    addIdentifiersFromValue(identifiers, controllerElement.dataset.controller)
  })

  return identifiers
}

// ★★ A lazily-imported controller chunk CAN fail to load, and when it does the damage is silent.
// The common cause is a deploy: Vite fingerprints every chunk, so a tab opened before the deploy
// asks for `submit_on_enter_controller.7d3db306.js`, which no longer exists, and the import
// rejects. PostHog caught exactly that on /inventory/:slug/edit (submit_on_enter, tinymce and
// ai_description) once client-side exception capture was switched on.
//
// Two separate bugs lived in the old three lines:
//   1. nothing caught the rejection, so the controller never registered and that part of the form
//      simply stopped working — no error, nothing for a dealer to act on. They click, nothing
//      happens, they click again. That is what the rage-click data on this page actually is.
//   2. the identifier was added to `registeredControllers` BEFORE the await, so a FAILED import
//      still marked it as registered — permanently. No later turbo:load and no MutationObserver
//      sighting would ever retry it. One blocked request killed that controller for the life of
//      the page.
// Now a failure rolls the mark back so the next sighting genuinely retries, and a chunk that is
// really gone (a deploy moved it) says so instead of leaving a dead form behind.
const failedControllerAttempts = new Map()
let staleAssetNoticeShown = false

// Vite/browsers word this several ways; match the family, not one sentence.
function looksLikeMissingChunk(error) {
  const message = (error && (error.message || String(error))) || ''
  return /dynamically imported module|Importing a module script failed|error loading|Failed to fetch|NetworkError/i.test(message)
}

function reportControllerFailure(identifier, error) {
  try {
    if (window.posthog && typeof window.posthog.captureException === 'function') {
      window.posthog.captureException(error, {
        controller: identifier,
        reason: 'controller_chunk_load_failed'
      })
    }
  } catch (e) { /* telemetry must never be the thing that breaks the page */ }
  try { console.error('[stimulus] controller failed to load:', identifier, error) } catch (e) {}
}

// Shown ONCE. A dealer whose form has quietly lost its behaviour cannot diagnose that, and
// retrying a chunk URL that a deploy deleted will never succeed — reloading is the only cure.
function showStaleAssetNotice() {
  if (staleAssetNoticeShown) return
  staleAssetNoticeShown = true
  try {
    if (document.getElementById('ml-stale-assets-notice')) return
    const bar = document.createElement('div')
    bar.id = 'ml-stale-assets-notice'
    bar.setAttribute('role', 'status')
    bar.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483000;' +
      'background:#0b5175;color:#fff;padding:10px 14px;font:14px/1.4 system-ui,sans-serif;' +
      'display:flex;gap:12px;align-items:center;justify-content:center;box-shadow:0 -2px 8px rgba(0,0,0,.2)'
    const text = document.createElement('span')
    text.textContent = 'A new version of MotorLot was released. Reload the page so everything on it works.'
    const reload = document.createElement('button')
    reload.type = 'button'
    reload.textContent = 'Reload'
    reload.style.cssText = 'background:#fff;color:#0b5175;border:0;border-radius:6px;' +
      'padding:6px 12px;font-weight:600;cursor:pointer'
    reload.addEventListener('click', function () { window.location.reload() })
    bar.appendChild(text)
    bar.appendChild(reload)
    ;(document.body || document.documentElement).appendChild(bar)
  } catch (e) { /* a notice that throws would be worse than no notice */ }
}

async function registerController(identifier) {
  if (registeredControllers.has(identifier)) return

  const controllerPath = controllerPathsByIdentifier[identifier]
  if (!controllerPath) return

  // Mark BEFORE the (async) import so a second sighting during the await can't
  // double-register the same identifier.
  registeredControllers.add(identifier)

  let controllerModule
  try {
    controllerModule = await controllerLoaders[controllerPath]()
  } catch (error) {
    // ★ Roll the mark back, or this identifier is dead for the rest of the page's life.
    registeredControllers.delete(identifier)

    const attempts = (failedControllerAttempts.get(identifier) || 0) + 1
    failedControllerAttempts.set(identifier, attempts)
    reportControllerFailure(identifier, error)

    // One failure can be a dropped request; a second on the same controller is a chunk that is
    // genuinely not there any more, and no amount of retrying will bring it back.
    if (attempts >= 2 && looksLikeMissingChunk(error)) showStaleAssetNotice()
    return
  }

  if (typeof controllerModule.default === 'function') {
    application.register(identifier, controllerModule.default)
  }
}

function loadControllersIn(element = document) {
  controllerIdentifiersIn(element).forEach(identifier => {
    registerController(identifier)
  })
}

loadControllersIn()
document.addEventListener('turbo:load', () => loadControllersIn())

new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) loadControllersIn(node)
    })
  })
}).observe(document.documentElement, { childList: true, subtree: true })
